import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building, ExternalLink, ArrowRightLeft } from "lucide-react";
import { BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { NaeringUtvikling } from "./NaeringUtvikling";
import { PendlingAvgChart } from "./PendlingAvgChart";
import { dec, num } from "@/lib/formatters";

const pctNum = (a, b) => (b ? (a / b) * 100 : null);
const pct = (a, b) => (b ? dec((a / b) * 100, 1) : "—");

const FlowList = ({ title, items, total, testId }) => (
  <div data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">{title}</p>
    <ul className="space-y-1.5">
      {items.slice(0, 6).map((it) => (
        <li key={it.code} className="text-sm">
          <div className="flex justify-between text-[#1F2A36]">
            <span>{it.name}</span>
            <span className="tabular-nums text-[#4B647F]">{fmt(it.count)} <span className="text-xs">({pct(it.count, total)} %)</span></span>
          </div>
          <div className="h-1.5 bg-[#F1F4F8] rounded-full mt-0.5"><div className="h-1.5 bg-[#103766] rounded-full" style={{ width: `${Math.min(100, (it.count / (items[0]?.count || 1)) * 100)}%` }} /></div>
        </li>
      ))}
    </ul>
  </div>
);

export const PendlingSection = ({ data }) => {
  if (!data?.year) return null;
  const d = data;
  const n = d.naering;
  const naeringRows = n
    ? Object.keys(n.arbeidssted || {})
        .filter((k) => k !== "00")
        .map((k) => ({ name: d.nace_labels?.[k] || k, "Arbeidsplassar i kommunen": n.arbeidssted[k] || 0, "Busette sysselsette": n.bosted?.[k] || 0 }))
        .sort((a, b) => b["Arbeidsplassar i kommunen"] - a["Arbeidsplassar i kommunen"])
    : [];
  const avg = d.averages;
  const avgText = (key) => (avg?.land ? `${avg.fylke ? `Fylke: ${num(avg.fylke[key])} % · ` : ""}Landet: ${num(avg.land[key])} %` : null);
  const kommunePct = {
    utpendling_pct: pctNum(d.utpendlere, d.sysselsatte_bosatte),
    innpendling_pct: pctNum(d.innpendlere, d.arbeidsplasser),
    arbeidsplassdekning_pct: pctNum(d.arbeidsplasser, d.sysselsatte_bosatte),
  };
  const hist = d.pendling_history || [];
  const histFirst = hist[0] || {};
  const histLast = hist[hist.length - 1] || {};
  const utDiff = (histLast.utpendlere || 0) - (histFirst.utpendlere || 0);
  const innDiff = (histLast.innpendlere || 0) - (histFirst.innpendlere || 0);
  const histChart = hist.map((r) => ({ year: r.year, "Pendlar ut": r.utpendlere, "Pendlar inn": r.innpendlere, Utpendlingsandel: r.utpendling_pct }));
  const tiles = [
    ["Arbeidsplassar i kommunen", fmt(d.arbeidsplasser), "personar som jobbar her (arbeidsstad)", "pendling-arbeidsplasser"],
    ["Sysselsette som bur her", fmt(d.sysselsatte_bosatte), "personar 15–74 år (bustad)", "pendling-sysselsatte"],
    ["Arbeidsplassdekning", `${num(kommunePct.arbeidsplassdekning_pct)} %`, "arbeidsplassar per 100 sysselsette busette", "pendling-dekning", avgText("arbeidsplassdekning_pct")],
    ["Bur og jobbar i kommunen", fmt(d.bor_og_jobber), `${pct(d.bor_og_jobber, d.sysselsatte_bosatte)} % av dei sysselsette som bur her`, "pendling-internt"],
    ["Pendlar ut", fmt(d.utpendlere), `personar · ${num(kommunePct.utpendling_pct)} % av dei sysselsette som bur her`, "pendling-ut", avgText("utpendling_pct")],
    ["Pendlar inn", fmt(d.innpendlere), `personar · ${num(kommunePct.innpendling_pct)} % av arbeidsplassane i kommunen`, "pendling-inn", avgText("innpendling_pct")],
    ["Netto pendling", `${d.innpendlere - d.utpendlere > 0 ? "+" : ""}${fmt(d.innpendlere - d.utpendlere)}`, d.innpendlere >= d.utpendlere ? "fleire pendlar inn enn ut" : "fleire pendlar ut enn inn", "pendling-netto"],
  ];
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="pendling-section" id="pendling">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5 text-[#103766]" />
          Arbeidsplassar, pendling og næringar {d.year}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Sysselsette 15–74 år, 4. kvartal. Kor jobbar dei som bur her, og kven jobbar i kommunen?</p>
      </CardHeader>
      <CardContent className="pt-6 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          {tiles.map(([label, value, sub, tid, avgLine]) => (
            <div key={tid} className="p-3 border border-[#E6E9F0] rounded-lg" data-testid={tid}>
              <p className="text-xs text-[#8AA4BF]">{label}</p>
              <p className="text-2xl font-light text-[#1F2A36]">{value}</p>
              <p className="text-[11px] text-[#4B647F]">{sub}</p>
              {avgLine && <p className="text-[11px] text-[#3F8A86] mt-1" data-testid={`${tid}-avg`}>{avgLine}</p>}
            </div>
          ))}
        </div>
        <PendlingAvgChart kommune={kommunePct} averages={avg} fylkeName={d.fylke_name} gruppeNamn={d.gruppe_namn} />
        {hist.length > 1 && (
          <div data-testid="pendling-over-tid">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
              Pendling over tid {hist[0].year}–{hist[hist.length - 1].year}
            </p>
            <p className="text-xs text-[#8AA4BF] mb-3" data-testid="pendling-over-tid-tekst">
              Utpendlinga har {utDiff === 0 ? "halde seg uendra på" : utDiff > 0 ? "auka frå" : "falle frå"}{" "}
              {utDiff === 0 ? `${fmt(histLast.utpendlere)} personar` : `${fmt(histFirst.utpendlere)} til ${fmt(histLast.utpendlere)} personar`} ({dec(histFirst.utpendling_pct, 1)} % →{" "}
              {dec(histLast.utpendling_pct, 1)} % av dei sysselsette som bur her). Innpendlinga har{" "}
              {innDiff === 0 ? "halde seg uendra" : innDiff > 0 ? `auka med ${fmt(innDiff)}` : `falle med ${fmt(Math.abs(innDiff))}`} personar i same periode.
            </p>
            <div className="h-[280px]" data-testid="pendling-over-tid-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={histChart} margin={{ right: 10 }}>
                  {endringsLinjer(histChart, { yAxisId: "p" })}
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
                  <YAxis yAxisId="p" stroke="#8AA4BF" fontSize={11} width={62} tickFormatter={fmt} domain={[(min) => Math.max(0, Math.round((min - 1200) / 500) * 500), (max) => Math.round((max + 1200) / 500) * 500]} />
                  <YAxis yAxisId="pct" orientation="right" stroke="#C0392B" fontSize={11} width={48} tickFormatter={(v) => `${num(v)} %`} domain={[(min) => Math.max(0, Math.floor(min - 4)), (max) => Math.ceil(max + 4)]} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => (n.includes("andel") ? `${dec(v, 1)} %` : `${fmt(v)} personar`)} />
                  <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
                  <Line yAxisId="p" type="monotone" dataKey="Pendlar ut" stroke="#103766" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line yAxisId="p" type="monotone" dataKey="Pendlar inn" stroke="#3F8A86" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line yAxisId="pct" type="monotone" dataKey="Utpendlingsandel" stroke="#C0392B" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FlowList title={`Kor pendlar innbyggjarane til? (${fmt(d.utpendlere)} utpendlarar)`} items={d.top_ut || []} total={d.utpendlere} testId="pendling-top-ut" />
          <FlowList title={`Kor kjem innpendlarane frå? (${fmt(d.innpendlere)} innpendlarar)`} items={d.top_inn || []} total={d.innpendlere} testId="pendling-top-inn" />
        </div>
        {naeringRows.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Næringar {n.year}: arbeidsplassar i kommunen mot busette sysselsette</p>
            <div className="h-[520px]" data-testid="naering-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={naeringRows} layout="vertical" barGap={1} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis type="number" stroke="#8AA4BF" fontSize={12} tickFormatter={fmt} />
                  <YAxis type="category" dataKey="name" width={230} stroke="#8AA4BF" fontSize={12} interval={0} />
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={tooltipStyle} />
                  <Legend itemSorter={null} />
                  <Bar dataKey="Arbeidsplassar i kommunen" fill="#103766" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="Busette sysselsette" fill="#8AA4BF" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-[#4B647F] mt-2">Størst næring etter arbeidsstad: <span className="font-medium" data-testid="naering-top">{naeringRows[0].name}</span> ({fmt(naeringRows[0]["Arbeidsplassar i kommunen"])} arbeidsplassar).
              {(n.estimert || []).length > 0 && (
                <span className="text-[#8AA4BF]" data-testid="naering-estimat-aar">
                  {" "}Tal som SSB skjuler av omsyn til personvern, er anslåtte: {n.estimert.map((c) => d.nace_labels?.[c] || c).join(", ")}.
                </span>
              )}
            </p>
          </div>
        )}
        <NaeringUtvikling history={d.naering_history} labels={d.nace_labels} />
        <p className="text-xs text-[#8AA4BF]">
          Kjelder: SSB <a href="https://www.ssb.no/statbank/table/03321" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">03321 <ExternalLink className="w-3 h-3" /></a> (pendlingsstraumar) og{" "}
          <a href="https://www.ssb.no/statbank/table/12539" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">12539 <ExternalLink className="w-3 h-3" /></a> (sysselsette etter næring, bustad og arbeidsstad).
        </p>
      </CardContent>
    </Card>
  );
};

export default PendlingSection;
